import { ChartConfiguration } from 'chart.js';
import {
	ReportBlockAnnotation,
	ReportBlockColumn,
	ReportBlockData,
	ReportBlockRender,
	ReportBlockSeries,
	ReportBlockShape
} from './report-blocks.service';
import {
	ENTITY_COLUMN_KEY,
	ENTITY_FIGURE_SEPARATOR
} from '../models/preview/preview.models';

export { ENTITY_COLUMN_KEY, ENTITY_FIGURE_SEPARATOR };
import {
	REPORT_PLATFORM_COLORS,
	REPORT_SENTIMENT_COLORS
} from './report-preview-chart-configs';
// The KPI callouts' own abbreviation ("8.4M"), so a stat card reads like the
// callout on the slide beside it.
import { formatCompactNumber } from './report-preview-utils';

/**
 * A resolved block as a Chart.js config.
 *
 * The bridge between the builder and the renderer the report already has: the
 * chart widget takes a whole `chartJsConfig` as data, so a block the backend
 * chose needs no component of its own — only this translation. Colours come
 * from the report's existing palette rather than a new one, so an AI-added
 * chart cannot be told apart from the sixteen fixed slides.
 *
 * Series keyed `positive` / `neutral` / `negative` keep the sentiment colours
 * the whole product uses; everything else walks the platform palette in order.
 *
 * A block may also be about SEVERAL entities — a brand and a competitor, or
 * two of the reader's own brands. That arrives as one series per entity and is
 * the one case where the defaults above are wrong in every respect: the
 * palette walk would repaint a brand the moment another block listed it
 * second, filled areas would paint over each other, and the shapes that spend
 * their whole drawing on a category have no channel left to put a second brand
 * in. Each of those is handled below, and each is a rule about the SHAPE
 * rather than about the data.
 *
 * And a block may be REDRAWN. A reader switches a chart's type and expects the
 * same numbers in another drawing, at once — so every render its shape allows
 * is drawn from the block already on the page, never read again: a time
 * series becomes a table of its days, a heatmap a line per weekday, a list of
 * accounts a ranked bar of its first measure. Nothing below invents a value to
 * make a type fit. Where a drawing needs one number per part, a part's number
 * is the SUM of what was counted for it, which is the one total that was
 * counted too; a shape whose numbers do not add up is never offered such a
 * drawing (the catalogue's `SHAPE_RENDERS` and `cannot`).
 */

const CHART_TYPE_BY_RENDER: Partial<
	Record<ReportBlockRender, ChartConfiguration['type']>
> = {
	line: 'line',
	area: 'line',
	bar: 'bar',
	stacked_bar: 'bar',
	ranked_bar: 'bar',
	pie: 'pie',
	doughnut: 'doughnut',
	polarArea: 'polarArea',
	wordCloud: 'wordCloud' as ChartConfiguration['type'],
	matrix: 'matrix' as ChartConfiguration['type']
};

const SENTIMENT_COLOR_BY_KEY: Record<string, string> = {
	positive: REPORT_SENTIMENT_COLORS.positive,
	neutral: REPORT_SENTIMENT_COLORS.neutral,
	negative: REPORT_SENTIMENT_COLORS.negative
};

/**
 * A series that is an ENTITY rather than a dimension of the data.
 *
 * The backend keys a compared series `brand:<uuid>` / `competitor:<uuid>` /
 * `industry:<uuid>`, and a block resolved for a single entity never carries
 * such a key. That is the only signal there is: the resolve response has no
 * entity list of its own, so a comparison is recognised by how it was encoded,
 * not declared. A chart set beside the period before it is a comparison too,
 * of two windows — keyed `period:current` / `period:previous` — and is drawn
 * by the same rules.
 */
const ENTITY_SERIES_KEY = /^(?:brand|competitor|industry|period):/;

/** What the backend puts between the entity and the metric in a figure's key. */

/** The column a merged table grows when it holds more than one entity. */

/**
 * Shapes where one slice IS one category, so the whole drawing is already
 * spent on the dimension and there is no channel left for a second entity.
 *
 * Handed two datasets, Chart.js draws them as nested rings in the SAME
 * category colours, under a legend that names the categories rather than the
 * brands — a picture of two things the reader cannot tell apart. Adding the
 * two brands into one 100% would be worse, because that is a share nobody
 * counted.
 *
 * So a comparison of these falls back to a GROUPED BAR: same labels, same
 * values, one bar per entity per category. Nothing is dropped and nothing is
 * invented — only the shape changes, and it changes to the one the catalogue
 * itself offers next for every block this can happen to (`mentions_by_platform`
 * and `mentions_by_language` default to doughnuts, `top_hashtags` and
 * `trending_conversations` to word clouds, and `bar` is the next render on all
 * four). `matrix` is deliberately absent: the one matrix block is not
 * comparable, so it can never arrive here with two entities.
 */
const SINGLE_ENTITY_TYPES: readonly string[] = [
	'pie',
	'doughnut',
	'polarArea',
	'wordCloud'
];

/**
 * The block whose ONE series divides a total between its entities — the
 * backend's share of voice — so its labels are the entities and each slice
 * is one of them. It is the comparison the fallback above must not touch: its
 * single ring already is the comparison, and grouped bars of one series would
 * be a bar chart of shares.
 */
export const REPORT_SHARE_OF_VOICE_BLOCK_ID = 'share_of_voice';

/** Whether a block's labels are its entities (see the constant above). */
export function isEntityShare(
	block: Pick<ReportBlockData, 'block_id'> | null | undefined
): boolean {
	return block?.block_id === REPORT_SHARE_OF_VOICE_BLOCK_ID;
}

/** How big a slice has to be to carry its share printed on it, in %. */
const MIN_LABELLED_SLICE = 5;

/** The renders that colour by part rather than by series. */
const SLICED_RENDERS: readonly ReportBlockRender[] = [
	'pie',
	'doughnut',
	'polarArea'
];

/** The weekday order every Social Listening heatmap reads in. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The label every ISO day starts with — how a time series is recognised. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

/** The lightest cell of a heatmap, so an empty hour still reads as the grid. */
const MATRIX_MIN_ALPHA = 0.12;

/** Word sizes in px: small enough to fit twenty, large enough to rank them. */
const WORD_MIN_SIZE = 14;
const WORD_MAX_SIZE = 44;

function seriesColor(series: ReportBlockSeries, index: number): string {
	return (
		series.color ??
		SENTIMENT_COLOR_BY_KEY[series.key] ??
		REPORT_PLATFORM_COLORS[index % REPORT_PLATFORM_COLORS.length]
	);
}

/** One colour per category, for the shapes where a slice is a category. */
function categoryColors(count: number): string[] {
	return Array.from(
		{ length: count },
		(_, index) =>
			REPORT_PLATFORM_COLORS[index % REPORT_PLATFORM_COLORS.length]
	);
}

function distinct(values: string[]): string[] {
	return Array.from(new Set(values));
}

// TRAP: never the palette's first colour. It is the report's accent, which the
// theme repaints over every chart (`recolorWidgetInPlace`): an entity holding
// it would take the theme's colour — the default theme's purple is this
// palette's third — and two entities would draw alike.
const ENTITY_COLORS = REPORT_PLATFORM_COLORS.slice(1);

/** A stable palette slot for a name. djb2 — cheapest hash that spreads short
    strings evenly, and the spread is the whole point. */
function paletteSlot(name: string): number {
	let hash = 5381;
	for (let index = 0; index < name.length; index++) {
		hash = ((hash << 5) + hash + name.charCodeAt(index)) | 0;
	}
	return Math.abs(hash) % ENTITY_COLORS.length;
}

/**
 * Who a block compares, in the order it names them — empty when it compares
 * nothing.
 *
 * A comparison shows up differently in each of the three shapes: a chart gets
 * one entity-keyed series each, a table gets an `entity` column, a KPI block
 * gets its figures prefixed with the entity. All three are read here so the
 * rest of the preview asks one question instead of three.
 *
 * All three need TWO names before this calls it a comparison. A block resolved
 * for a single entity comes back byte-for-byte the block it was before
 * comparison existed, and has to keep rendering that way.
 */
export function blockEntityNames(block: ReportBlockData): string[] {
	// TRAP: a share of voice compares its entities in ONE series; read as a
	// comparison here, it would be redrawn as bars and advised against a ring.
	if (isEntityShare(block)) {
		return [];
	}
	const fromSeries = distinct(
		(block.series ?? [])
			.filter(series => ENTITY_SERIES_KEY.test(series.key))
			.map(series => series.label)
	);
	if (fromSeries.length > 1) {
		return fromSeries;
	}

	const fromFigures = distinct(
		Object.keys(block.figures ?? {})
			.filter(key => key.includes(ENTITY_FIGURE_SEPARATOR))
			.map(key => key.slice(0, key.indexOf(ENTITY_FIGURE_SEPARATOR)))
	);
	if (fromFigures.length > 1) {
		return fromFigures;
	}

	const comparedTable = (block.columns ?? []).some(
		column => column.key === ENTITY_COLUMN_KEY
	);
	const fromRows = comparedTable
		? distinct(
				(block.rows ?? []).map(row =>
					String(row[ENTITY_COLUMN_KEY] ?? '')
				)
		  ).filter(Boolean)
		: [];
	return fromRows.length > 1 ? fromRows : [];
}

/**
 * Who a block is about when it is about several, as its heading says it: the
 * entities of a comparison, and the slices of a share of voice.
 */
export function blockSubjectNames(block: ReportBlockData): string[] {
	return isEntityShare(block)
		? distinct(block.labels ?? [])
		: blockEntityNames(block);
}

/**
 * One colour per entity — the same colour in every block of the report.
 *
 * Walking the palette by series index repaints an entity the moment a block
 * lists it second, so one brand would be blue on the mentions chart and cyan
 * on the reach chart of the same document. The colour is therefore DERIVED
 * FROM THE NAME: the same entity hashes to the same palette slot wherever it
 * appears, and nothing has to remember what it was given last — which matters,
 * because the PDF export renders each block in a browser that shares no state
 * with the preview the reader approved.
 *
 * The name rather than the `brand:<uuid>` key, because the name is the only
 * identity present in all three shapes — a table cell and a figure's prefix
 * carry no uuid — and colour has to agree across them.
 *
 * Collisions take the next free slot, and the names are walked SORTED so that
 * resolution cannot depend on the order a block happened to list them in;
 * otherwise "Costco, Sam's Club" and "Sam's Club, Costco" would settle
 * differently and the guarantee would break exactly where it is needed. The
 * probe also stops once every slot is taken, because a report that hung the
 * headless exporter would be unrecoverable.
 */
export function assignEntityColors(names: string[]): Map<string, string> {
	const colors = new Map<string, string>();
	const taken = new Set<number>();

	for (const name of distinct(names).sort()) {
		let slot = paletteSlot(name);
		while (taken.has(slot) && taken.size < ENTITY_COLORS.length) {
			slot = (slot + 1) % ENTITY_COLORS.length;
		}
		taken.add(slot);
		colors.set(name, ENTITY_COLORS[slot]);
	}

	return colors;
}

/**
 * The same colour, see-through.
 *
 * Two filled areas both run to the origin, so the one drawn second paints over
 * the one drawn first: the reader gets a single brand's chart with someone
 * else's outline on top. Chart.js has no "blend these" option, so the fill has
 * to be translucent for both to survive. The border stays solid, which is what
 * keeps each line readable where the fills overlap.
 */
function translucent(color: string, alpha = 0.18): string {
	const hex = color.replace('#', '');
	const red = parseInt(hex.slice(0, 2), 16);
	const green = parseInt(hex.slice(2, 4), 16);
	const blue = parseInt(hex.slice(4, 6), 16);
	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function isChartRender(render: ReportBlockRender): boolean {
	return Boolean(CHART_TYPE_BY_RENDER[render]);
}

/**
 * How a block's numbers are laid out.
 *
 * The backend says so on every block it resolves. It is worked out here only
 * for a block that does not carry it — one read before the field existed —
 * from the same evidence the resolver itself writes: figures are headline
 * numbers, rows with no series are a list (or, with a day and an hour, a
 * heatmap), and labels that are all days are a time series.
 */
export function blockShape(block: ReportBlockData): ReportBlockShape {
	if (block.shape) {
		return block.shape;
	}
	if (Object.keys(block.figures ?? {}).length) {
		return 'kpi';
	}
	if (!block.series?.length && block.rows?.length) {
		const keys = new Set((block.columns ?? []).map(column => column.key));
		return keys.has('day') && keys.has('hour') ? 'matrix' : 'rows';
	}
	const labels = block.labels ?? [];
	return labels.length && labels.every(label => ISO_DAY.test(label))
		? 'series'
		: 'categories';
}

/** What a chart draws: labels along one axis, a series of values per line. */
interface Drawable {
	labels: string[];
	series: ReportBlockSeries[];
	/** One colour per label, when a bar or a slice IS a label. */
	labelColors?: string[];
}

interface MatrixCell {
	day: string;
	hour: number;
	value: number;
}

function matrixCells(block: ReportBlockData): MatrixCell[] {
	return (block.rows ?? [])
		.map(row => ({
			day: String(row['day'] ?? ''),
			hour: Number(row['hour']),
			value: Number(row['value'])
		}))
		.filter(
			cell =>
				WEEKDAYS.includes(cell.day) &&
				Number.isInteger(cell.hour) &&
				Number.isFinite(cell.value)
		);
}

/** 0 → "12am", 19 → "7pm": the hour the way the heatmap slide writes it. */
export function reportHourLabel(hour: number): string {
	const twelve = hour % 12 === 0 ? 12 : hour % 12;
	return `${twelve}${hour < 12 ? 'am' : 'pm'}`;
}

/**
 * A heatmap as one series per weekday, across the hours it has values for.
 *
 * Every cell appears exactly once, so the numbers are the heatmap's own. An
 * hour a day has no cell for is a GAP, not a zero: the resolver leaves out the
 * hours nobody engaged in, and a line dropping to zero there would draw a
 * measurement where there was none. A weekday with no cells at all is left
 * out rather than drawn as a legend entry with nothing behind it.
 */
function matrixAsSeries(block: ReportBlockData): Drawable | null {
	const cells = matrixCells(block);
	if (!cells.length) {
		return null;
	}
	const hours = Array.from(new Set(cells.map(cell => cell.hour))).sort(
		(left, right) => left - right
	);
	const byCell = new Map(
		cells.map(cell => [`${cell.day}|${cell.hour}`, cell.value])
	);
	const series = WEEKDAYS.filter(day =>
		cells.some(cell => cell.day === day)
	).map(day => ({
		key: day.toLowerCase(),
		label: day,
		values: hours.map(hour => byCell.get(`${day}|${hour}`) ?? null)
	}));
	return { labels: hours.map(reportHourLabel), series };
}

function toNumber(value: unknown): number | null {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}

/**
 * A ranked list as bars of its first measure.
 *
 * The list's own ranking and its own top N, one bar per row: an account and
 * its mentions, a site and its mentions. A compared list is still each
 * entity's own top N, concatenated (see `comparison.py`), so the entity goes
 * on the label and on the bar's colour rather than into a ranking the two
 * lists were never merged into.
 */
function rowsAsSeries(block: ReportBlockData): Drawable | null {
	const columns = block.columns ?? [];
	const name = columns.find(
		column => column.kind === 'text' && column.key !== ENTITY_COLUMN_KEY
	);
	const measure = columns.find(column => column.kind === 'number');
	const rows = block.rows ?? [];
	if (!name || !measure || !rows.length) {
		return null;
	}

	const compared = columns.some(column => column.key === ENTITY_COLUMN_KEY);
	const entityOf = (row: Record<string, string | number>): string =>
		String(row[ENTITY_COLUMN_KEY] ?? '');
	const colors = compared ? assignEntityColors(rows.map(entityOf)) : null;

	return {
		labels: rows.map(row =>
			compared
				? `${row[name.key] ?? ''}${ENTITY_FIGURE_SEPARATOR}${entityOf(
						row
				  )}`
				: String(row[name.key] ?? '')
		),
		series: [
			{
				key: measure.key,
				label: measure.label,
				values: rows.map(row => toNumber(row[measure.key]))
			}
		],
		labelColors: colors
			? rows.map(
					row =>
						colors.get(entityOf(row)) ?? REPORT_PLATFORM_COLORS[0]
			  )
			: undefined
	};
}

function drawableOf(
	block: ReportBlockData,
	shape: ReportBlockShape
): Drawable | null {
	switch (shape) {
		case 'matrix':
			return matrixAsSeries(block);
		case 'rows':
			return rowsAsSeries(block);
		case 'kpi':
			return null;
		default:
			return block.series?.length
				? {
						labels: block.labels ?? [],
						series: block.series,
						labelColors: block.labelColors
				  }
				: null;
	}
}

/** Everything counted for one series, or null when none of it was. */
function totalOf(values: Array<number | null>): number | null {
	const present = values.filter(
		(value): value is number => value !== null && Number.isFinite(value)
	);
	return present.length
		? present.reduce((sum, value) => sum + value, 0)
		: null;
}

/** Everything counted for the label at `index`, across every series. */
function labelTotal(series: ReportBlockSeries[], index: number): number {
	return series.reduce((sum, one) => sum + (one.values[index] ?? 0), 0);
}

/**
 * The parts a pie, a word cloud or a stat card is made of.
 *
 * One series: its labels — a slice per platform, a card per country. Several:
 * the series themselves, each as everything counted for it — sentiment over a
 * month as a pie is the positive, neutral and negative split of that month,
 * which is a question those numbers really answer, where nested rings of
 * thirty days answer none.
 */
function partsOf(drawable: Drawable): {
	labels: string[];
	values: Array<number | null>;
	colors: string[];
	units: Array<'%' | undefined>;
	collapsed: boolean;
} {
	if (drawable.series.length > 1) {
		return {
			labels: drawable.series.map(series => series.label),
			values: drawable.series.map(series => totalOf(series.values)),
			colors: drawable.series.map((series, index) =>
				seriesColor(series, index)
			),
			units: drawable.series.map(series => series.unit),
			collapsed: true
		};
	}
	const only = drawable.series[0];
	return {
		labels: drawable.labels,
		values: only.values,
		colors: drawable.labelColors ?? categoryColors(drawable.labels.length),
		units: drawable.labels.map(() => only.unit),
		collapsed: false
	};
}

/** Labels (and everything that runs along them) ordered by their totals. */
function rankedByTotal(drawable: Drawable): Drawable {
	const order = drawable.labels
		.map((_, index) => index)
		.sort(
			(left, right) =>
				labelTotal(drawable.series, right) -
				labelTotal(drawable.series, left)
		);
	return {
		labels: order.map(index => drawable.labels[index]),
		series: drawable.series.map(series => ({
			...series,
			values: order.map(index => series.values[index] ?? null)
		})),
		labelColors: drawable.labelColors
			? order.map(index => drawable.labelColors![index])
			: undefined
	};
}

/**
 * A number as a card or a cell says it.
 *
 * Whole counts in full up to a million, where the digits stop being read and
 * start being counted; a share keeps one decimal and its percent sign. A value
 * that could not be read says so rather than printing a zero.
 */
export function formatReportFigure(value: number | null, unit?: '%'): string {
	if (value === null || !Number.isFinite(value)) {
		return '—';
	}
	if (unit === '%') {
		return `${(Math.round(value * 10) / 10).toLocaleString('en-US')}%`;
	}
	if (Math.abs(value) >= 1_000_000) {
		return formatCompactNumber(value);
	}
	return (Math.round(value * 10) / 10).toLocaleString('en-US');
}

export function buildBlockChartConfig(
	block: ReportBlockData
): ChartConfiguration | null {
	const requested = CHART_TYPE_BY_RENDER[block.render];
	if (!requested) {
		return null;
	}

	const shape = blockShape(block);
	if (block.render === 'matrix') {
		return shape === 'matrix' ? buildMatrixConfig(block) : null;
	}

	const read = drawableOf(block, shape);
	const drawable =
		read && isEntityShare(block) ? withEntityColors(read) : read;
	if (!drawable?.series.length) {
		return null;
	}

	const entities = blockEntityNames(block);
	const comparing = entities.length > 1;
	const entityColors = comparing ? assignEntityColors(entities) : null;

	// See SINGLE_ENTITY_TYPES: a compared pie is not a pie.
	const render: ReportBlockRender =
		comparing && SINGLE_ENTITY_TYPES.includes(requested as string)
			? 'bar'
			: block.render;

	if (render === 'wordCloud') {
		return buildWordCloudConfig(drawable);
	}
	if (SLICED_RENDERS.includes(render)) {
		return buildSlicedConfig(drawable, render, block);
	}
	return buildCartesianConfig(drawable, render, block, entityColors);
}

/**
 * A share of voice's parts in the colours their entities have on the report's
 * other charts, so a rival reads the same on its slice as on its line.
 */
function withEntityColors(drawable: Drawable): Drawable {
	const colors = assignEntityColors(drawable.labels);
	return {
		...drawable,
		labelColors: drawable.labels.map(
			label => colors.get(label) ?? REPORT_PLATFORM_COLORS[0]
		)
	};
}

/**
 * The counts a share of voice divides, by entity, from the table the answer
 * carries beside its shares. Null for a label the table does not hold.
 */
function shareCounts(
	block: ReportBlockData,
	labels: string[]
): Array<number | null> {
	const counts = new Map(
		(block.rows ?? []).map(row => [
			String(row[ENTITY_COLUMN_KEY] ?? ''),
			toNumber(row['mentions'])
		])
	);
	return labels.map(label => counts.get(label) ?? null);
}

/** A tooltip line that says a share is a share: " Costco: 60%". */
function percentTooltipLabel(context: any): string {
	const name = context.dataset?.label ? `${context.dataset.label}: ` : '';
	return ` ${name}${formatReportFigure(toNumber(context.raw), '%')}`;
}

/**
 * Lines, areas and bars — standing, stacked or ranked.
 *
 * A ranked bar is a bar chart laid on its side with its labels ordered by what
 * was counted for them, largest first, the way a leaderboard reads; every
 * series keeps its own bar, so nothing a grouped bar would show is folded away.
 */
function buildCartesianConfig(
	source: Drawable,
	render: ReportBlockRender,
	block: ReportBlockData,
	entityColors: Map<string, string> | null
): ChartConfiguration {
	const ranked = render === 'ranked_bar';
	const drawable = ranked ? rankedByTotal(source) : source;
	const type = CHART_TYPE_BY_RENDER[render] as ChartConfiguration['type'];
	// Only a block that ASKED to be stacked stacks. Grouping is what a
	// comparison needs by default: stacked, the bar the reader measures is two
	// brands added together, which is a total no axis explains unless the
	// reader chose it — and choosing it is what switching to stacked bars is.
	const stacked = render === 'stacked_bar';
	const filled = render === 'area';
	// Translucent whenever more than one area is drawn, not only for
	// comparisons: `sentiment_over_time` as an area has the same three-way
	// over-paint, and it had it before comparison existed.
	const softFill = filled && drawable.series.length > 1;
	// A bar per label in the label's own colour, when the section beside it
	// names that colour already — the country table's dots, the channel list.
	const perLabel =
		type === 'bar' &&
		drawable.series.length === 1 &&
		Boolean(drawable.labelColors?.length);

	const datasets = drawable.series.map((series, index) => {
		const color =
			entityColors?.get(series.label) ?? seriesColor(series, index);
		return {
			label: series.label,
			data: series.values,
			borderColor: perLabel ? drawable.labelColors : color,
			// A lone area gets no fill of its own: the chart widget's
			// `restoreAreaFills` builds the fade under the line from its
			// colour, the way the fixed slides' areas are drawn, where a solid
			// fill painted the whole area as one block.
			backgroundColor: perLabel
				? drawable.labelColors
				: softFill
				? translucent(color)
				: filled
				? undefined
				: color,
			borderWidth: 2,
			fill: filled,
			tension: 0.35,
			pointRadius: 0,
			// The legend reads POINT colours, not line colours, whenever
			// `usePointStyle` is on — and with no point colour set it falls
			// through to Chart.js's own defaults. Live, that drew a pink dot
			// beside an orange line and a blue dot beside a green one: a legend
			// naming the wrong brand, which is worse than no legend at all.
			pointBackgroundColor: color,
			pointBorderColor: color,
			borderRadius: type === 'bar' ? 4 : 0
		};
	});

	const shares =
		drawable.series.length > 0 &&
		drawable.series.every(series => series.unit === '%');
	const valueAxis = {
		stacked,
		beginAtZero: true,
		grid: { color: 'rgba(0, 0, 0, 0.06)' },
		...(shares
			? { ticks: { callback: (value: number | string) => `${value}%` } }
			: {})
	};
	// TRAP: none on a ranked bar — it reorders its labels, and a mark's index
	// would land on another day.
	const marks = ranked ? [] : blockDayMarks(block, drawable.labels);
	const labelAxis = {
		stacked,
		grid: { display: false },
		// A ranked list is read label by label, so every one of them shows.
		...(ranked ? { ticks: { autoSkip: false } } : {})
	};

	return {
		type,
		data: { labels: drawable.labels, datasets },
		options: {
			responsive: true,
			maintainAspectRatio: false,
			indexAxis: ranked ? 'y' : 'x',
			plugins: {
				// One series needs no legend — the block's own title says what
				// it is, and a legend reading "Mentions" above a chart titled
				// "Mentions over time" is a line of noise. A comparison is the
				// opposite case and the reason this is a condition rather than
				// a constant: two unnamed lines answer none of the question
				// that was asked, which was which brand is which. A section
				// that already prints its series above the chart is the third
				// case: a second legend under it would say the same thing twice.
				legend: {
					display:
						drawable.series.length > 1 && !block.seriesLegendShown,
					position: 'bottom',
					labels: { usePointStyle: true, boxWidth: 8, padding: 16 }
				},
				tooltip: shares
					? {
							enabled: true,
							callbacks: { label: percentTooltipLabel }
					  }
					: { enabled: true },
				// CONTRACT: drawn by the chart widget's plugin of this id.
				...(marks.length ? { reportDayMarks: { marks } } : {})
			},
			scales: ranked
				? { x: valueAxis, y: labelAxis }
				: { x: labelAxis, y: valueAxis }
		}
	} as ChartConfiguration;
}

/** One marked day of a trend: where it sits on the axis, and its number. */
export interface ReportBlockDayMark {
	/** Its position in the chart's labels. */
	index: number;
	/** "1", "2", "3": the mark on the chart and its line under it agree. */
	number: string;
	annotation: ReportBlockAnnotation;
}

/**
 * The days a trend marks (`BlockData.annotations`), numbered in the order they
 * fall, each at its place among `labels`. A day the chart does not draw has no
 * place to be marked and is left out, and the numbers skip nothing.
 */
export function blockDayMarks(
	block: Pick<ReportBlockData, 'annotations' | 'labels'>,
	labels: readonly string[] = block.labels ?? []
): ReportBlockDayMark[] {
	const marks: ReportBlockDayMark[] = [];
	for (const annotation of block.annotations ?? []) {
		const index = labels.indexOf(annotation.date);
		if (index >= 0) {
			marks.push({ index, number: String(marks.length + 1), annotation });
		}
	}
	return marks;
}

/** Pies, doughnuts and polar areas: one ring, one slice per part. */
function buildSlicedConfig(
	drawable: Drawable,
	render: ReportBlockRender,
	block: ReportBlockData
): ChartConfiguration {
	const parts = partsOf(drawable);
	const share = isEntityShare(block) && !parts.collapsed;
	// TRAP: a share of voice divides COUNTS, and its legend says them — the
	// PDF has no tooltip to hover for how many mentions 80% is.
	const counts = share ? shareCounts(block, parts.labels) : [];
	const labels = share
		? parts.labels.map((label, index) =>
				counts[index] === null
					? label
					: `${label}${ENTITY_FIGURE_SEPARATOR}${formatReportFigure(
							counts[index]
					  )}`
		  )
		: parts.labels;
	const percentages =
		parts.labels.length > 0 && parts.units.every(unit => unit === '%');
	return {
		type: CHART_TYPE_BY_RENDER[render] as ChartConfiguration['type'],
		data: {
			labels,
			datasets: [
				{
					label: parts.collapsed
						? block.title
						: drawable.series[0].label,
					data: parts.values,
					backgroundColor: parts.colors,
					borderColor: '#FFFFFF',
					borderWidth: 2
				}
			]
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				// A slice has no axis to name it, so the legend is how a
				// reader knows which is which — unless the slices ARE the
				// series the section already names above the chart.
				legend: {
					display: !(parts.collapsed && block.seriesLegendShown),
					position: 'bottom',
					labels: { usePointStyle: true, boxWidth: 8, padding: 16 }
				},
				tooltip: percentages
					? {
							enabled: true,
							callbacks: { label: percentTooltipLabel }
					  }
					: { enabled: true },
				// CONTRACT: drawn by the chart widget's segmentLabels plugin, as
				// the fixed sections' pies print their shares.
				...(share
					? {
							segmentLabels: {
								display: true,
								color: '#FFFFFF',
								fontFamily: 'Gilroy',
								fontSize: 14,
								fontWeight: '700',
								minPercentageToShow: MIN_LABELLED_SLICE
							}
					  }
					: {})
			},
			scales:
				render === 'polarArea'
					? { r: { ticks: { display: false } } }
					: undefined
		}
	} as ChartConfiguration;
}

/**
 * A word per part, sized by what was counted for it.
 *
 * The word cloud takes a FONT SIZE per word, not a count, so the counts are
 * scaled into a readable range — on a square root, which keeps the gap
 * between the largest word and the tenth something the eye can still rank —
 * and carried beside the sizes so the tooltip says the number, not the pixels.
 * A part nothing was counted for has no word: an absent hashtag drawn at the
 * smallest size would claim a mention it does not have.
 */
function buildWordCloudConfig(drawable: Drawable): ChartConfiguration | null {
	const parts = partsOf(drawable);
	const words = parts.labels
		.map((label, index) => ({ label, weight: parts.values[index] ?? 0 }))
		.filter(word => word.weight > 0);
	if (!words.length) {
		return null;
	}
	const heaviest = Math.max(...words.map(word => word.weight));
	const weights = words.map(word => word.weight);

	return {
		type: 'wordCloud' as ChartConfiguration['type'],
		data: {
			labels: words.map(word => word.label),
			datasets: [
				{
					label: parts.collapsed ? '' : drawable.series[0].label,
					data: words.map(
						word =>
							WORD_MIN_SIZE +
							(WORD_MAX_SIZE - WORD_MIN_SIZE) *
								Math.sqrt(word.weight / heaviest)
					),
					// One colour, and the report's accent, so the theme
					// repaints it like any other accent (the chart widget's
					// word-cloud plugin applies it to every word).
					color: seriesColor(drawable.series[0], 0),
					weights,
					rotate: 0,
					padding: 3,
					family: 'Gilroy',
					weight: 600,
					fit: true
				}
			]
		} as unknown as ChartConfiguration['data'],
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						label: (context: any) =>
							` ${context.label}: ${formatReportFigure(
								weights[context.dataIndex] ?? null,
								parts.units[0]
							)}`
					}
				}
			}
		}
	} as ChartConfiguration;
}

/**
 * The day-by-hour grid, one cell per filled hour.
 *
 * Tinted by value against the busiest cell, in the report's accent — carried
 * as a plain string on the dataset rather than baked into the colour function,
 * so the theme repaints it the way it repaints every other accent (a function
 * is not something the recolour can see into).
 */
function buildMatrixConfig(block: ReportBlockData): ChartConfiguration | null {
	const cells = matrixCells(block);
	if (!cells.length) {
		return null;
	}
	const hours = Array.from({ length: 24 }, (_, hour) => hour);
	const busiest = Math.max(0, ...cells.map(cell => cell.value));

	return {
		type: 'matrix' as ChartConfiguration['type'],
		data: {
			datasets: [
				{
					label: block.title,
					data: cells.map(cell => ({
						x: reportHourLabel(cell.hour),
						y: cell.day,
						v: cell.value
					})),
					baseColor: REPORT_PLATFORM_COLORS[0],
					backgroundColor: (context: any) => {
						const value = context.raw?.v ?? 0;
						const share = busiest > 0 ? value / busiest : 0;
						return translucent(
							context.dataset.baseColor,
							MATRIX_MIN_ALPHA + (1 - MATRIX_MIN_ALPHA) * share
						);
					},
					borderColor: '#FFFFFF',
					borderWidth: 1,
					width: (context: any) => {
						const area = context.chart.chartArea;
						return area
							? (area.right - area.left) / hours.length
							: 0;
					},
					height: (context: any) => {
						const area = context.chart.chartArea;
						return area
							? (area.bottom - area.top) / WEEKDAYS.length
							: 0;
					}
				}
			]
		} as unknown as ChartConfiguration['data'],
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						title: () => '',
						label: (context: any) =>
							` ${context.raw.y} ${
								context.raw.x
							}: ${formatReportFigure(context.raw.v)}`
					}
				}
			},
			scales: {
				x: {
					type: 'category',
					labels: hours.map(reportHourLabel),
					offset: true,
					grid: { display: false },
					ticks: { maxRotation: 0, autoSkip: true }
				},
				y: {
					type: 'category',
					labels: WEEKDAYS,
					offset: true,
					grid: { display: false }
				}
			}
		} as any
	} as ChartConfiguration;
}

/** A table: the columns it has, the rows under them, in `labels` order. */
export interface ReportBlockTable {
	columns: ReportBlockColumn[];
	rows: Array<Record<string, string | number | null>>;
}

/**
 * A block as a table, whatever it was drawn as before.
 *
 * A block that already carries rows — a breakdown, a list, a comparison of
 * lists — is its own table. Everything else is turned into one here, from the
 * same numbers: a series becomes a row per day and a column per line, a
 * heatmap a row per weekday and a column per hour (a pivot, because 168 rows
 * of "day, hour, value" is a table nobody reads), and headline figures a row
 * each.
 */
export function blockTable(block: ReportBlockData): ReportBlockTable {
	const shape = blockShape(block);
	if (shape === 'matrix') {
		return matrixTable(block);
	}
	if (shape === 'kpi') {
		return {
			columns: [
				{ key: 'measure', label: 'Measure', kind: 'text' },
				{ key: 'value', label: 'Value', kind: 'text' }
			],
			rows: Object.entries(block.figures ?? {}).map(
				([measure, value]) => ({ measure, value })
			)
		};
	}
	if (block.rows?.length && block.columns?.length) {
		return { columns: block.columns, rows: block.rows };
	}

	const series = block.series ?? [];
	return {
		columns: [
			{
				key: 'label',
				label:
					block.labelColumn ?? (shape === 'series' ? 'Date' : 'Name'),
				kind: 'text'
			},
			...series.map(
				(one, index): ReportBlockColumn => ({
					key: `s${index}`,
					label:
						one.unit === '%' && !one.label.includes('%')
							? `${one.label}, %`
							: one.label,
					kind: 'number'
				})
			)
		],
		rows: (block.labels ?? []).map((label, at) => {
			const row: Record<string, string | number | null> = { label };
			series.forEach((one, index) => {
				const value = one.values[at] ?? null;
				row[`s${index}`] =
					value === null ? null : Math.round(value * 10) / 10;
			});
			return row;
		})
	};
}

function matrixTable(block: ReportBlockData): ReportBlockTable {
	const cells = matrixCells(block);
	const hours = Array.from(new Set(cells.map(cell => cell.hour))).sort(
		(left, right) => left - right
	);
	const byCell = new Map(
		cells.map(cell => [`${cell.day}|${cell.hour}`, cell.value])
	);
	return {
		columns: [
			{ key: 'day', label: 'Day', kind: 'text' },
			...hours.map(
				(hour): ReportBlockColumn => ({
					key: `h${hour}`,
					label: reportHourLabel(hour),
					kind: 'number'
				})
			)
		],
		rows: WEEKDAYS.filter(day => cells.some(cell => cell.day === day)).map(
			day => {
				const row: Record<string, string | number | null> = { day };
				hours.forEach(hour => {
					row[`h${hour}`] = byCell.get(`${day}|${hour}`) ?? null;
				});
				return row;
			}
		)
	};
}

/**
 * A block as headline figures, label → formatted value.
 *
 * Headline blocks carry their own, formatted by the backend. For the rest a
 * card is a part (see `partsOf`) and what was counted for it: a card per
 * country, or a card per sentiment with its total for the period. A time
 * series of one line is one card, its total — a card per day is a table.
 */
export function blockFigures(block: ReportBlockData): Record<string, string> {
	if (Object.keys(block.figures ?? {}).length) {
		return block.figures;
	}
	const shape = blockShape(block);
	const drawable = drawableOf(block, shape);
	if (!drawable?.series.length) {
		return {};
	}

	const figures: Record<string, string> = {};
	if (shape === 'series' && drawable.series.length === 1) {
		const only = drawable.series[0];
		figures[only.label] = formatReportFigure(
			totalOf(only.values),
			only.unit
		);
		return figures;
	}
	const parts = partsOf(drawable);
	parts.labels.forEach((label, index) => {
		figures[label] = formatReportFigure(
			parts.values[index] ?? null,
			parts.units[index]
		);
	});
	return figures;
}
